import React from 'react';
import { motion, Variants } from 'framer-motion';
import Constants from '../../../app/utils/constants';
import challengeCoin from '../../../assets/images/cryptonary/thechallenge_coin.webp';
import colors from '../../theme/color';

const ChallengeSection = () => {
  const { cryptonaryChallengePoints } = Constants;

  const lineVariants: Variants = {
    hiddenLeft: { scaleX: 0, originX: 1 },
    hiddenRight: { scaleX: 0, originX: 0 },
    visible: { 
      scaleX: 1, 
      transition: { duration: 1.35, ease: [0.22, 1, 0.36, 1], delay: 0.26 } 
    }
  };

  const cardVariants: Variants = {
    hiddenLeft: { opacity: 0, x: -100 },
    hiddenRight: { opacity: 0, x: 100 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.14,
        duration: 1.15,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }),
  };

  const challengeCoinVariants: Variants = {
    initial: { y: 0 },
    animate: {
      y: [0, -10, 0],
      x: [0, 4, 0, -3, 0],
      rotateY: [0, 180, 360],
      rotateX: [0, 12, 0],
      scale: [1, 1.14, 1],
      rotateZ: [0, 2, 0],
      transition: {
        duration: 2.1,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section className="relative z-20 w-full pb-24 pt-5 md:pb-28 lg:pb-32 overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(180deg, ${colors.cryptonarySectionBase}00 0%, ${colors.cryptonarySectionBase}75 34%, ${colors.cryptonarySectionBase}D6 68%, ${colors.cryptonarySectionBase} 100%)`,
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(180deg, ${colors.cryptonaryChallengeOverlayA}00 0%, ${colors.cryptonaryChallengeOverlayA}1F 22%, ${colors.cryptonaryChallengeOverlayB}29 42%, ${colors.cryptonaryChallengeOverlayC}1A 62%, ${colors.cryptonaryChallengeOverlayD}00 100%)`,
        }}
      />
      
      <div className="mx-auto w-full max-w-[1320px] px-4 md:px-5 lg:max-w-[1180px] lg:px-6 min-[1441px]:max-w-[1320px] min-[1536px]:max-w-[1120px] min-[1550px]:max-w-[1120px] min-[1681px]:max-w-[1320px] 2xl:max-w-[1650px] min-[1536px]:px-10 min-[1550px]:px-10 min-[1681px]:px-6 min-[1900px]:w-[97vw] min-[1900px]:max-w-none min-[1900px]:px-3 min-[2560px]:w-[98vw] min-[2560px]:max-w-none min-[2560px]:px-6">

        <div className="relative">
        <div className="relative mx-auto flex w-fit items-center gap-2 sm:gap-4 md:gap-5">

  {/* LEFT LINE */}
  <motion.span
    variants={lineVariants}
    initial="hiddenLeft"
    whileInView="visible"
    viewport={{ once: false }}
    className="
      relative
      h-px
      w-[45px]
      sm:w-[45px]
      md:w-[62px]
      lg:w-[78px]
    "
    style={{
      backgroundColor: `${colors.cryptonaryLineSoftMint}BF`,
    }}
  >
    <span
      className="
        absolute
        left-0
        top-1/2
        h-[4px]
        w-[4px]
        sm:h-[5px]
        sm:w-[5px]
        md:h-[6px]
        md:w-[6px]
        -translate-x-1/2
        -translate-y-1/2
        rounded-full
      "
      style={{
        backgroundColor: `${colors.cryptonaryLineSoftMint}E6`,
      }}
    />
  </motion.span>

  {/* TITLE */}
  <motion.h2
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: false }}
    transition={{
      duration: 0.95,
      ease: [0.22, 1, 0.36, 1],
    }}
    className="
      font-gilroy-bold
      text-[1.5rem]
      sm:text-[2rem]
      md:text-[2.3rem]
      lg:text-[2.5rem]
      leading-none
      text-center
    "
    style={{
      color: colors.cryptonaryAccentGreen,
    }}
  >
    The Challenge
  </motion.h2>

  {/* RIGHT LINE */}
  <motion.span
    variants={lineVariants}
    initial="hiddenRight"
    whileInView="visible"
    viewport={{ once: false }}
    className="
      relative
      h-px
      w-[45px]
      sm:w-[45px]
      md:w-[62px]
      lg:w-[78px]
    "
    style={{
      backgroundColor: `${colors.cryptonaryLineSoftMint}BF`,
    }}
  >
    <span
      className="
        absolute
        right-0
        top-1/2
        h-[4px]
        w-[4px]
        sm:h-[5px]
        sm:w-[5px]
        md:h-[6px]
        md:w-[6px]
        translate-x-1/2
        -translate-y-1/2
        rounded-full
      "
      style={{
        backgroundColor: `${colors.cryptonaryLineSoftMint}E6`,
      }}
    />
  </motion.span>

</div>

          <div className="relative mt-9 px-2 py-5 sm:mt-10 sm:px-3 md:mt-11 md:px-4 md:py-6 min-[1536px]:origin-top min-[1536px]:scale-[0.9] min-[1550px]:origin-top min-[1550px]:scale-[0.9] min-[1681px]:scale-100">
            <div className="relative grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-2 md:grid-flow-col md:grid-rows-3 md:gap-x-6 md:gap-y-6 lg:gap-x-8 lg:gap-y-7 2xl:gap-x-8 2xl:gap-y-7 min-[1900px]:gap-x-10 min-[1900px]:gap-y-9 min-[2560px]:gap-x-14 min-[2560px]:gap-y-12">
              {cryptonaryChallengePoints.map((point, index) => {
                const isLeftSide = index < 3;
                return (
                  <motion.article
                    key={point.serial}
                    custom={index}
                    variants={cardVariants}
                    initial={isLeftSide ? "hiddenLeft" : "hiddenRight"}
                    whileInView="visible"
                    whileHover="hover"
                    viewport={{ once: false, amount: 0.2 }}
                    className="group relative min-h-[88px] overflow-hidden rounded-[12px] px-5 py-4 sm:px-6 sm:py-5 cursor-pointer transition-all duration-500 2xl:min-h-[98px] 2xl:px-7 2xl:py-5 min-[1900px]:min-h-[120px] min-[1900px]:px-8 min-[1900px]:py-7 min-[2560px]:min-h-[170px] min-[2560px]:rounded-[18px] min-[2560px]:px-12 min-[2560px]:py-10"
                    style={{
                      backgroundImage: `linear-gradient(124deg, ${colors.cryptonaryChallengeCardA}26 10%, ${colors.cryptonaryChallengeCardB}14 52%, ${colors.cryptonaryChallengeCardC}FC 100%)`,
                    }}
                  >
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        backgroundImage: `linear-gradient(124deg, ${colors.cryptonaryChallengeCardA}40 10%, ${colors.cryptonaryChallengeCardB}25 52%, ${colors.cryptonaryAccentGreen}30 100%)`,
                      }}
                    />

                    <span
                      className="pointer-events-none absolute right-1 top-[70%] -translate-y-1/2 z-20 font-gilroy-bold text-[4.5rem] leading-none tracking-[-0.03em] opacity-[0.09] sm:right-2 sm:text-[5.2rem] group-hover:opacity-[1] group-hover:scale-110 transition-all duration-700 ease-out"
                      style={{ color: colors.cryptonaryAccentGreen }}
                    >
                      {point.serial}
                    </span>
                    
                    <p className="relative z-10 max-w-[72%] font-gilroy-medium font-normal md:text-lg lg:text-[20px] leading-[1.5] tracking-normal text-[#e5f0e9] 2xl:text-[1.25rem] min-[1900px]:text-[1.55rem] min-[2560px]:text-[2.2rem]">
                      {point.lines.map((line) => (
                        <span key={line} className="block">
                          {line}
                        </span>
                      ))}
                    </p>
                  </motion.article>
                );
              })}
            </div>
            <motion.img
              variants={challengeCoinVariants}
              initial="initial"
              animate="animate"
              src={challengeCoin}
              alt="Challenge token coin"
              className="pointer-events-none absolute md:right-4 md:top-[60%] right-1 bottom-20 z-0 hidden w-[82px] object-contain md:block lg:-right-8 lg:bottom-12 lg:w-[96px] 2xl:-right-14 2xl:bottom-18 2xl:w-[140px] min-[1900px]:-right-16 min-[1900px]:bottom-20 min-[1900px]:w-[158px] min-[2560px]:-right-24 min-[2560px]:bottom-36 min-[2560px]:w-[200px]"
              style={{ filter: `drop-shadow(0 14px 28px ${colors.cryptonaryAccentGreen}5C)`, transformPerspective: 1200 }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChallengeSection;
